import React, { useRef } from 'react';
import { motion, useInView } from 'framer-motion';
import { Rocket, Microscope, Code, Database, Award } from 'lucide-react';
import { useTheme } from '../ThemeContext';
import SectionHeader from './SectionHeader';
import TiltCard from './TiltCard';

const experiences = [
  {
    title: 'Research Assistant - ML & Data Analysis',
    company: 'Laboratory for Atmospheric and Space Physics',
    date: 'Feb 2025 - Present',
    icon: Rocket,
    color: '#ff00ff',
    cleanColor: '#6366f1',
    bullets: [
      'Developed ML model using Python and PySPEDAS to analyze Van Allen Probe plasma wave data, achieving 95% agreement with traditional methods',
      'Analyzed 7+ years of Van Allen Probe data using advanced processing techniques for plasma wave identification',
      'Findings presented at GEM and AGU conferences',
    ],
    tags: ['Python', 'PySPEDAS', 'ML', 'Data Analysis'],
  },
  {
    title: 'Research Assistant - Computational Biology',
    company: 'Biofrontiers Institute, CU Boulder',
    date: 'Aug 2025 - Present',
    icon: Microscope,
    color: '#bf00ff',
    cleanColor: '#8b5cf6',
    bullets: [
      'Applied statistical algorithms to analyze firefly synchronization patterns from field data using Python',
      'Processed 3+ years of firefly behavioral data to classify synchrony patterns and extract quantitative insights',
      'Implemented ML models for automated pattern recognition in biological systems',
    ],
    tags: ['Python', 'Statistics', 'ML', 'Biology'],
  },
  {
    title: 'Software Engineer - CheatMaps',
    company: 'CU Boulder Senior Capstone',
    date: 'Aug 2025 - Present',
    icon: Code,
    color: '#ff2d95',
    cleanColor: '#ec4899',
    bullets: [
      'Collaborating with 7-member team to develop React-based web application for Bureau of Land Management to track invasive cheatgrass',
      'Architecting backend infrastructure and database systems for geospatial data collection and real-time monitoring',
      'Integrating ML model for automated cheatgrass detection to enhance field data collection efficiency',
    ],
    tags: ['React', 'Geospatial', 'ML', 'Full-Stack'],
  },
  {
    title: 'Program Analyst',
    company: "NASA L'Space MCA",
    date: 'Sept 2024 - Dec 2024',
    icon: Award,
    color: '#00d4ff',
    cleanColor: '#06b6d4',
    bullets: [
      'Designed complete mock rover mission analyzing Lunar lava tubes in a 16-member multi-disciplined team',
      'Created advanced project schedules and budget models using data-driven analysis techniques',
      'Awarded top analyst among 700+ academy members for quantitative analysis and project management',
    ],
    tags: ['Project Management', 'Data Analysis', 'NASA'],
  },
  {
    title: 'Data Entry Specialist',
    company: 'WiseTek Solutions',
    date: 'Dec 2021 - May 2022',
    icon: Database,
    color: '#8888aa',
    cleanColor: '#9ca3af',
    bullets: [
      'Managed multiple database systems to maintain comprehensive inventory tracking and data integrity',
      'Developed processes to increase operational efficiency by streamlining data workflows',
    ],
    tags: ['Database', 'Data Management'],
  },
];

function TimelineItem({ exp, index }) {
  const ref = useRef(null);
  const isInView = useInView(ref, { once: true, margin: '-50px' });
  const { isCyberpunk } = useTheme();
  const isLeft = index % 2 === 0;
  const Icon = exp.icon;
  const accentColor = isCyberpunk ? exp.color : exp.cleanColor;

  const card = (
    <div style={{ perspective: '1200px' }}>
    <TiltCard className={`p-6 group transition-all duration-500 ${
      isCyberpunk
        ? 'neon-border corner-accents bg-cyber-dark/60 hover:bg-cyber-dark/80'
        : 'bg-white border border-gray-200 rounded-xl shadow-sm hover:shadow-lg'
    }`}>
      <div className="font-mono text-xs tracking-wider mb-2" style={{ color: accentColor }}>
        {exp.date}
      </div>
      <h3 className={`font-display text-lg font-semibold mb-1 tracking-wide transition-colors duration-500 ${
        isCyberpunk ? 'text-white' : 'text-gray-900'
      }`}>{exp.title}</h3>
      <p className={`font-body text-sm mb-4 transition-colors duration-500 ${
        isCyberpunk ? 'text-cyber-dim/80' : 'text-gray-500'
      }`}>@ {exp.company}</p>

      <ul className="space-y-2 mb-4">
        {exp.bullets.map((bullet, i) => (
          <li key={i} className={`flex items-start gap-2 text-sm font-body transition-colors duration-500 ${
            isCyberpunk ? 'text-cyber-dim/70' : 'text-gray-600'
          }`}>
            <span style={{ color: accentColor }} className="mt-1.5 text-xs">
              {isCyberpunk ? '▹' : '•'}
            </span>
            <span>{bullet}</span>
          </li>
        ))}
      </ul>

      <div className="flex flex-wrap gap-2">
        {exp.tags.map((tag) => (
          <span key={tag} className={`font-mono text-xs px-2 py-1 transition-all duration-500 ${
            isCyberpunk
              ? 'bg-cyber-magenta/5 border border-cyber-magenta/15 text-cyber-magenta/70'
              : 'bg-indigo-50 text-indigo-600 rounded-md'
          }`}>{tag}</span>
        ))}
      </div>
    </TiltCard>
    </div>
  );

  return (
    <div ref={ref} className="grid grid-cols-[1fr_48px_1fr] items-start w-full mb-12 last:mb-0">
      {/* Left slot */}
      {isLeft ? (
        <motion.div
          initial={{ opacity: 0, x: -50 }}
          animate={isInView ? { opacity: 1, x: 0 } : {}}
          transition={{ duration: 0.6, delay: 0.3 }}
          className="pr-8"
        >
          {card}
        </motion.div>
      ) : <div />}

      {/* Icon centered on the spine */}
      <motion.div
        initial={{ scale: 0 }}
        animate={isInView ? { scale: 1 } : {}}
        transition={{ duration: 0.4, delay: 0.2 }}
        className={`relative z-10 w-12 h-12 flex items-center justify-center transition-all duration-500 ${
          isCyberpunk
            ? 'bg-cyber-dark border'
            : 'bg-white border-2 rounded-xl shadow-md'
        }`}
        style={{ borderColor: `${accentColor}${isCyberpunk ? '66' : ''}` }}
      >
        <Icon size={18} style={{ color: accentColor }} />
      </motion.div>

      {/* Right slot */}
      {!isLeft ? (
        <motion.div
          initial={{ opacity: 0, x: 50 }}
          animate={isInView ? { opacity: 1, x: 0 } : {}}
          transition={{ duration: 0.6, delay: 0.3 }}
          className="pl-8"
        >
          {card}
        </motion.div>
      ) : <div />}
    </div>
  );
}

export default function Experience() {
  const { isCyberpunk } = useTheme();

  return (
    <section id="experience" className={`relative py-24 px-6 transition-colors duration-700 ${
      isCyberpunk ? 'bg-cyber-black' : 'bg-white'
    }`}>
      <div className="max-w-6xl mx-auto">
        <SectionHeader number="02." title="EXPERIENCE" mb="mb-16" />
        <div className="relative">
          {/* Single continuous center spine */}
          <div className={`absolute left-1/2 -translate-x-[0.5px] top-0 bottom-0 w-[1px] transition-colors duration-500 ${
            isCyberpunk
              ? 'bg-gradient-to-b from-cyber-magenta/40 via-cyber-magenta/20 to-transparent'
              : 'bg-gradient-to-b from-gray-300 via-gray-200 to-transparent'
          }`} />

          {experiences.map((exp, i) => (
            <TimelineItem key={i} exp={exp} index={i} />
          ))}
        </div>
      </div>

      {isCyberpunk ? (
        <div className="cyber-divider mt-24 max-w-4xl mx-auto" />
      ) : (
        <div className="mt-24 max-w-4xl mx-auto h-[1px] bg-gradient-to-r from-transparent via-gray-200 to-transparent" />
      )}
    </section>
  );
}
